import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import type { PickupLocation } from "@gaole/shared";
import { formatPickupScheduledAt } from "@gaole/shared";
import { api } from "../../lib/api";
import { Badge, Button, PageLoader, Select, statusColor } from "../../components/ui";
import { orderStatusLabel } from "@gaole/shared";

interface CalendarOrder {
  id: string;
  orderNo: string;
  customer: string;
  status: string;
  locationId: string | null;
  locationName: string | null;
  scheduledAt: string;
}

interface CalendarDay {
  date: string;
  orders: CalendarOrder[];
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function monthRange(year: number, month: number) {
  const from = new Date(year, month, 1);
  const to = new Date(year, month + 1, 0, 23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

function calendarCells(year: number, month: number) {
  const first = new Date(year, month, 1);
  const lastDate = new Date(year, month + 1, 0).getDate();
  const startPad = (first.getDay() + 6) % 7;
  const cells: { date: string | null; day: number | null }[] = [];
  for (let i = 0; i < startPad; i++) cells.push({ date: null, day: null });
  for (let d = 1; d <= lastDate; d++) {
    const date = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ date, day: d });
  }
  return cells;
}

export function PickupCalendarTab() {
  const anchor = new Date();
  const [year, setYear] = useState(anchor.getFullYear());
  const [month, setMonth] = useState(anchor.getMonth());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [locationId, setLocationId] = useState("");

  const range = monthRange(year, month);

  const locationsQuery = useQuery({
    queryKey: ["pickup-locations"],
    queryFn: () => api<{ locations: PickupLocation[] }>("/shipping/pickup-options").then((r) => r.locations),
  });

  const calendarQuery = useQuery({
    queryKey: ["fulfilment-pickup-calendar", range.from, range.to, locationId],
    queryFn: () => {
      const params = new URLSearchParams({ from: range.from, to: range.to });
      if (locationId) params.set("locationId", locationId);
      return api<{ days: CalendarDay[] }>(`/fulfilment/pickups/calendar?${params}`);
    },
  });

  const ordersByDay = useMemo(() => {
    const map = new Map<string, CalendarOrder[]>();
    for (const day of calendarQuery.data?.days ?? []) {
      map.set(day.date, day.orders);
    }
    return map;
  }, [calendarQuery.data?.days]);

  const selectedOrders = selectedDay ? (ordersByDay.get(selectedDay) ?? []) : [];

  function shiftMonth(delta: number) {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
    setSelectedDay(null);
  }

  const cells = calendarCells(year, month);
  const monthLabel = new Date(year, month, 1).toLocaleString("en-MY", { month: "long", year: "numeric" });

  if (calendarQuery.isLoading && !calendarQuery.data) {
    return <PageLoader />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
      <div className="min-w-0 flex-1 rounded-2xl border border-line bg-surface p-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => shiftMonth(-1)} aria-label="Previous month">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <p className="font-display text-sm font-semibold">{monthLabel}</p>
            <Button variant="ghost" size="sm" onClick={() => shiftMonth(1)} aria-label="Next month">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Select
            label=""
            value={locationId}
            onChange={(e) => {
              setLocationId(e.target.value);
              setSelectedDay(null);
            }}
            className="min-w-[12rem]"
          >
            <option value="">All locations</option>
            {(locationsQuery.data ?? []).map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </Select>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-muted">
          {WEEKDAYS.map((d) => (
            <div key={d} className="py-1">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((cell, idx) => {
            if (!cell.date || cell.day == null) {
              return <div key={`pad-${idx}`} className="min-h-[4.5rem] rounded-lg bg-transparent" />;
            }
            const count = ordersByDay.get(cell.date)?.length ?? 0;
            const selected = selectedDay === cell.date;
            return (
              <button
                key={cell.date}
                type="button"
                onClick={() => setSelectedDay(cell.date)}
                className={`min-h-[4.5rem] rounded-lg border p-2 text-left transition-colors ${
                  selected
                    ? "border-brand-pink/50 bg-brand-pink/[0.08]"
                    : "border-line bg-surface-2 hover:bg-hover"
                }`}
              >
                <span className="text-xs font-semibold tabular-nums">{cell.day}</span>
                {count > 0 && (
                  <p className="mt-1 text-[10px] font-medium text-brand-pink">
                    {count} pickup{count === 1 ? "" : "s"}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <aside className="w-full shrink-0 rounded-2xl border border-line bg-surface p-4 lg:w-80">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted">Scheduled pickups</p>
        {!selectedDay ? (
          <p className="mt-3 text-sm text-muted">Select a day to see orders and times.</p>
        ) : selectedOrders.length === 0 ? (
          <p className="mt-3 text-sm text-muted">No pickups on {selectedDay}.</p>
        ) : (
          <ul className="mt-3 space-y-3">
            {selectedOrders.map((o) => (
              <li key={o.id} className="rounded-xl border border-line bg-surface-2 p-3 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-mono text-xs font-semibold">{o.orderNo}</span>
                  <Badge color={statusColor(o.status)}>{orderStatusLabel(o.status)}</Badge>
                </div>
                <p className="mt-1 font-medium">{o.customer}</p>
                {o.locationName && <p className="text-xs text-muted">{o.locationName}</p>}
                <p className="mt-1 text-xs text-brand-pink">{formatPickupScheduledAt(o.scheduledAt)}</p>
              </li>
            ))}
          </ul>
        )}
      </aside>
    </div>
  );
}
